/**
 * THE CAR, AS IT GOES INTO A SENTENCE WE SEND A CUSTOMER.
 *
 * Anthony: "why are we sending so many quotes with no vehicle?"
 *
 * Six texts went out on 8 September reading "here's the quote for the No
 * vehicle: $225.00", and the chase behind them read "did you get the quote
 * for the your car?". Both come from the same mistake, made twice.
 *
 * vehicleName() is a LABEL. "No vehicle" is exactly right in a table cell
 * and a disaster in a sentence, and because it returns that string rather
 * than an empty one, the `|| "car"` fallback written next to it could never
 * fire — a non-empty string is truthy. The chase had the mirror problem: it
 * wrote `the ${car}` around a value that was already "your car".
 *
 * So this returns a NOUN PHRASE WITH ITS OWN ARTICLE, and callers never add
 * one. "the 2019 Honda Pilot" or "your car" — both read correctly after
 * "the quote for", which is the only place either is used.
 *
 * A QUOTE WITH NO CAR IS NOT ALWAYS A MISTAKE. A pre-purchase inspection is
 * a customer asking us to look at a car they do not own yet, and most of
 * them cannot tell us a year, make and model because they are standing in
 * somebody's driveway deciding. "your car" is wrong for them too, which is
 * why an unknown car reads as "the inspection" when we know that is what it
 * is, and only falls back to "your car" when we know nothing at all.
 */

import { words } from "./trade.ts";

export type CarLike = {
  year?: number | string | null;
  make?: string | null;
  model?: string | null;
  description?: string | null;
} | null | undefined;

/** Year make model, or the free-text description, or "" when there is none. */
export function carLabel(v: CarLike): string {
  if (!v) return "";
  const ymm = [v.year, v.make, v.model]
    .map((x) => String(x ?? "").trim())
    .filter(Boolean)
    .join(" ")
    .trim();
  return ymm || String(v.description ?? "").trim();
}

/**
 * Ready to drop straight after "the quote for". Never add an article to
 * what comes back, and never test it for emptiness — it is always a phrase
 * that reads.
 */
export function carPhrase(v: CarLike, opts?: { prePurchase?: boolean; trade?: string | null }): string {
  const label = carLabel(v);
  /* A garage door shop's customer reads this sentence too, and "the quote
     for your car" is the same class of mistake as "the quote for the No
     vehicle" -- it is confident and it is about the wrong object. The
     shop's own word comes from its trade; an unset trade is 'auto', so
     every existing shop reads exactly as it did. */
  if (!label) return opts?.prePurchase ? "the inspection" : words(opts?.trade).theirs;
  /* description is free text somebody typed, and "the white transit" is a
     perfectly normal thing to have typed. Adding our own article to it
     gives "the the white transit". */
  return /^(the|a|an|my|your|his|her|their)\s/i.test(label) ? label : `the ${label}`;
}
